import { CreateOrderRequest, Price } from '@/types'
import React, { useMemo } from 'react'
import { CldImage } from 'next-cloudinary'
import { Label } from '../ui/label'
import { formatPrice } from '@/helper/formatter'
import { Button } from '../ui/button'
import { Minus, Plus } from 'lucide-react'
import { Input } from '../ui/input'
import { getSeasonalPrice } from '@/helper/utils'
import { useTranslation } from 'next-i18next'

// DEV-492 — thumbnail of the inventory product next to each rate
// in the staff booking flow, so HC operators can choose visually.
const RATE_THUMBNAIL_SIZE_PX = 40

const PriceProductThumbnail = ({ price }: { price: Price }) => {
  const publicId = price.product?.photos?.[0]
  if (!publicId) {
    return null
  }
  return (
    <CldImage
      src={publicId}
      width={RATE_THUMBNAIL_SIZE_PX}
      height={RATE_THUMBNAIL_SIZE_PX}
      crop="thumb"
      alt={price.name}
      className="rounded-md shrink-0"
    />
  )
}

const PriceUnitLabel = ({
  price,
  selectedDate,
  displayStock,
  isVoucherMode,
  hidePrice = false,
}: {
  price: Price
  selectedDate: Date
  displayStock?: number
  isVoucherMode: boolean
  /**
   * Voucher REDEMPTION mode (≠ isVoucherMode which is voucher PURCHASE).
   * The guest already paid via the bundle — showing "Gratuito" / a euro
   * amount on the unit row is misleading. Render only the unit name.
   */
  hidePrice?: boolean
}) => {
  const { t } = useTranslation('experience')

  const priceRate = useMemo(
    () =>
      isVoucherMode ? price.retailRate : getSeasonalPrice(price, selectedDate),
    [selectedDate, price, isVoucherMode]
  )

  return (
    <div>
      <Label tooltip={price.description}>{price.name}</Label>
      {!hidePrice && (
        <span className="capitalize">
          {displayStock != undefined
            ? t('common:remaining_stock', { stock: displayStock })
            : price.isFree && !price.hideFreeLabel
            ? t('common:free')
            : price.isFree && price.hideFreeLabel
            ? null
            : formatPrice(priceRate) + '/' + price.unitName}
        </span>
      )}
    </div>
  )
}

const PriceCard = ({
  selectedDate,
  price,
  values,
  setFieldValue,
  isIndividualStock,
  availablePrices,
  remainGroupStock,
  quantityByProductId,
  isExtra,
  isBlocking,
  actionable,
  hidePrice = false,
}: {
  selectedDate: Date
  price: Price
  values: CreateOrderRequest
  setFieldValue: any
  isIndividualStock: boolean
  availablePrices: Record<string, number> | undefined
  remainGroupStock: number
  quantityByProductId: Record<number, number>
  isExtra?: boolean
  isBlocking: boolean
  actionable: boolean
  /** Suppresses the per-unit price label. Used in voucher redemption mode. */
  hidePrice?: boolean
}) => {
  const { t } = useTranslation('experience')

  const noStock = isIndividualStock
    ? (availablePrices?.[price.id] ?? 0) === 0
    : remainGroupStock === 0 && availablePrices?.[0] === 0

  const EXTRA_NO_INVENTORY_LIMIT = 99
  const remainingStock = () => {
    if (isExtra) {
      if (isBlocking) {
        return 0
      }

      // DEV-661: when the extra is backed by an inventoried product, the
      // backend exposes the available count under availablePrices[price.id].
      // Without it (legacy "fee-only" extras), keep the permissive cap.
      if (price.productId && availablePrices?.[price.id] !== undefined) {
        return (
          availablePrices[price.id] -
          (quantityByProductId[price.productId as any] ?? 0)
        )
      }

      return EXTRA_NO_INVENTORY_LIMIT
    }

    if (isIndividualStock) {
      const maxQuantity = availablePrices?.[price.id] ?? 0

      return maxQuantity - (quantityByProductId[price.productId as any] ?? 0)
    }

    return remainGroupStock
  }

  const isIncreaseDisabled = () => {
    return remainingStock() <= 0
  }

  // DEV-492 — wrap the label with the inventory product thumbnail so the
  // three render branches (read-only, sold-out, interactive) share the same
  // left-hand layout. Returns null thumb when no photo, keeping legacy spacing.
  const rowLabel = (extraProps: Partial<React.ComponentProps<typeof PriceUnitLabel>> = {}) => (
    <div className="flex items-center gap-3 min-w-0">
      <PriceProductThumbnail price={price} />
      <PriceUnitLabel
        price={price}
        selectedDate={selectedDate}
        isVoucherMode={values.isBuyingVoucher ?? false}
        hidePrice={hidePrice}
        {...extraProps}
      />
    </div>
  )

  if (!actionable) {
    return (
      <div className="flex items-center justify-between text-sm">
        {rowLabel()}

        {remainingStock() > 0 ? (
          <div>
            {values.selectedPrices[price.id] ? (
              <Input
                value={values.selectedPrices[price.id] ?? 0}
                className="w-12 text-center"
                readOnly
              />
            ) : null}
          </div>
        ) : (
          <div>{t('sold_out')}</div>
        )}
      </div>
    )
  }

  if (noStock) {
    return (
      <div className="flex items-center justify-between text-sm">
        {rowLabel({ displayStock: isBlocking ? remainingStock() : undefined })}

        <div>{t('Full')}</div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between text-sm">
      {rowLabel({ displayStock: isBlocking ? remainingStock() : undefined })}

      <div className="flex items-center space-x-2">
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full"
          type="button"
          disabled={(values.selectedPrices[price.id] ?? 0) === 0}
          onClick={() => {
            let nextSelectedPrices = { ...values.selectedPrices }

            if (values.selectedPrices[price.id] > 1) {
              nextSelectedPrices[price.id] = values.selectedPrices[price.id] - 1
            } else {
              delete nextSelectedPrices[price.id]
            }
            setFieldValue('selectedPrices', nextSelectedPrices)
          }}
        >
          <Minus />
        </Button>
        <Input
          value={values.selectedPrices[price.id] ?? 0}
          className="w-12 text-center"
          min={0}
          readOnly
          onChange={(e) =>
            setFieldValue('selectedPrices', {
              ...values.selectedPrices,
              [price.id]: Number(e.target.value),
            })
          }
        />
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full"
          type="button"
          disabled={isIncreaseDisabled()}
          onClick={() => {
            setFieldValue('selectedPrices', {
              ...values.selectedPrices,
              [price.id]: (values.selectedPrices[price.id] ?? 0) + 1,
            })
          }}
        >
          <Plus />
        </Button>
      </div>
    </div>
  )
}

export default PriceCard
